import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ServiceItem } from "@/lib/services";
import { SERVICE_LINKS, AREA_LINKS, SEGMENT_LINKS } from "@/lib/link-index.gen";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { ServicePillarShell } from "@/components/ServicePillarShell";
import { loadServicePillar } from "@/content/service-pillars/lazy.gen";
import { loadPillarDepth, type PillarDepth } from "@/content/service-pillars/depth-lazy";
import type { ServicePillar } from "@/content/service-pillars/types";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { getEnSlugForThaiSlug } from "@/content/en-services/pair.gen";
import { NOTARY_KEYWORDS_TOP as NOTARY_KEYWORDS } from "@/content/notary-corpus/top.gen";
import { FaqSourceNote, type FaqSourceTopic } from "@/components/FaqSourceNote";
import { serviceHeroAbsolute, getServiceHero } from "@/content/service-pillars/hero-images";

/** Map a service category to the matching official-source citation set. */
function sourceTopicForCategory(category: string): FaqSourceTopic {
  if (category === "notary") return "notary";
  if (category === "translation") return "translation";
  if (category === "background") return "police-clearance";
  if (category === "business" || category === "corporate") return "business";
  return "consular";
}

function safeArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

export const Route = createFileRoute("/services/$service")({
  loader: async ({
    params,
  }): Promise<{
    svc: ServiceItem;
    pillar: ServicePillar | null;
    depth: PillarDepth | null;
    cat: { label: string; sub?: string } | null;
    areaSlugs: string[];
    segmentSlugs: string[];
  }> => {
    const { SERVICES, CATEGORIES } = await import("@/lib/services");
    const svc = SERVICES.find((s) => s.slug === params.service);
    if (!svc) throw notFound();
    // Loaded lazily so the 1.1MB pillar corpus never lands in the entry chunk.
    const pillar = (await loadServicePillar(svc.slug)) ?? null;
    if (pillar) {
      const depth = await loadPillarDepth(pillar.slug);
      return { svc, pillar, depth, cat: null, areaSlugs: [], segmentSlugs: [] };
    }
    const [{ PHUKET_AREA_PAGES }, { PHUKET_SEGMENTS }] = await Promise.all([
      import("@/lib/phuket-area-pages"),
      import("@/lib/phuket-segments"),
    ]);
    const found = CATEGORIES.find((c) => c.id === svc.category);
    return {
      svc,
      pillar,
      depth: null,
      cat: found ? { label: found.label, sub: found.sub } : null,
      areaSlugs: PHUKET_AREA_PAGES.filter(
        (a) => a.topServices.includes(svc.slug) || a.topServices.includes(svc.id),
      )
        .slice(0, 9)
        .map((a) => a.slug),
      segmentSlugs: PHUKET_SEGMENTS.filter(
        (seg) => seg.services.includes(svc.slug) || seg.services.includes(svc.id),
      )
        .slice(0, 6)
        .map((s) => s.slug),
    };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.svc;
    if (!s) return { meta: [{ title: "Service not found" }] };
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const pillar = loaderData?.pillar ?? null;
    const title = pillar?.metaTitle ?? `${s.title} ใน${config.nameTh} | Thai Notary ${config.nameEn}`;
    const desc = pillar?.metaDesc ?? `${s.desc} — บริการใน${config.nameTh}. ${s.priceFrom ? `เริ่ม ${s.priceFrom.toLocaleString()} บาท.` : ""} จองทางโทรศัพท์หรือ LINE`;
    const enSlug = getEnSlugForThaiSlug(s.slug);
    const heroCategory = pillar?.category ?? s.category;
    const hero = getServiceHero(heroCategory);
    const base = pageHeadForProvince({
      title,
      description: desc,
      path: `/services/${s.slug}`,
      type: "article",
      image: serviceHeroAbsolute(heroCategory, site),
      imageAlt: hero.altTh,
      alternates: enSlug ? [{ lang: "en", path: `/en/services/${enSlug}` }] : undefined,
      keywords: s.slug === "notary-public" ? NOTARY_KEYWORDS.slice(0, 60) : undefined,
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บริการ", url: "/services" },
              { name: s.title, url: `/services/${s.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/services/${s.slug}#service`,
            name: s.title,
            serviceType: s.titleEn,
            description: s.desc,
            url: `${site}/services/${s.slug}`,
            image: serviceHeroAbsolute(heroCategory, site),
            areaServed: { "@type": "Place", name: `${config.nameEn}, Thailand` },
            provider: { "@id": `${site}/#organization` },
            ...(s.priceFrom && {
              offers: {
                "@type": "Offer",
                priceCurrency: "THB",
                price: s.priceFrom,
                ...(s.priceTo && { priceSpecification: { "@type": "PriceSpecification", minPrice: s.priceFrom, maxPrice: s.priceTo, priceCurrency: "THB" } }),
              },
            }),
          }),
        },
        ...(pillar
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: safeArray(pillar.faqs).map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: pillar.h1,
                  description: pillar.tldr,
                  totalTime: "PT1H",
                  estimatedCost: {
                    "@type": "MonetaryAmount",
                    currency: "THB",
                    value: s.priceFrom ?? 1500,
                  },
                  tool: [
                    { "@type": "HowToTool", name: "Passport / ID" },
                    { "@type": "HowToTool", name: "Original document" },
                  ],
                  step: safeArray(pillar.process).map((p) => ({
                    "@type": "HowToStep",
                    position: p.step,
                    name: p.title,
                    text: p.body,
                  })),
                }),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "@id": `${site}/services/${s.slug}#webpage`,
                  url: `${site}/services/${s.slug}`,
                  speakable: {
                    "@type": "SpeakableSpecification",
                    cssSelector: ["h1", "#tldr", "#pricing", "#faq"],
                    xpath: ["/html/head/title"],
                  },
                }),
              },
              ...(safeArray(pillar.pricing).length
                ? [
                    {
                      type: "application/ld+json",
                      children: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "OfferCatalog",
                        name: `${s.title} — ตารางราคา`,
                        itemListElement: safeArray(pillar.pricing).map((p, i) => ({
                          "@type": "Offer",
                          position: i + 1,
                          name: p.item,
                          price:
                            typeof p.price === "string"
                              ? p.price.replace(/[^\d]/g, "").slice(0, 6) || undefined
                              : p.price,
                          priceCurrency: "THB",
                          description: p.note,
                          availability: "https://schema.org/InStock",
                        })),
                      }),
                    },
                  ]
                : []),
            ]
          : []),
      ],
    };
  },
  component: ServicePage,
  notFoundComponent: () => (
    <div className="p-10 text-center"><h1 className="text-2xl font-bold">ไม่พบบริการนี้</h1><Link to="/services" className="text-gold">← กลับ</Link></div>
  ),
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error("Service page error:", error);
    return (
      <div className="p-10 text-center">
        <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
        <p className="mt-2 text-sm text-muted-foreground">ไม่สามารถโหลดหน้าได้ กรุณาลองใหม่อีกครั้ง</p>
        <a href="/services" className="mt-4 inline-block text-gold">← กลับ</a>
      </div>
    );
  },
});

function ServicePage() {
  const { svc, pillar, depth, cat, areaSlugs, segmentSlugs } = Route.useLoaderData();
  const { config } = getActiveProvince();

  if (pillar) return <ServicePillarShell pillar={pillar} depth={depth} />;
  const related = SERVICE_LINKS.filter((s) => s.category === svc.category && s.slug !== svc.slug).slice(0, 6);
  const segments = SEGMENT_LINKS.filter((seg) => segmentSlugs.includes(seg.slug));
  const areas = AREA_LINKS.filter((a) => areaSlugs.includes(a.slug));
  return (
    <ProvincePageShell
      eyebrow={cat?.label ?? "Service"}
      title={`${svc.title} ใน${config.nameTh}`}
      subtitle={svc.desc}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "บริการ", to: "/services" }, { label: svc.title }]}
    >
      <div className="mx-auto max-w-7xl space-y-14 px-4 py-16">
        <section className="grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gold">ราคา</div>
            <div className="mt-1 text-xl font-black text-primary">
              {svc.priceFrom ? `${svc.priceFrom.toLocaleString()}${svc.priceTo ? ` – ${svc.priceTo.toLocaleString()}` : ""} บาท` : "ติดต่อรับใบเสนอราคา"}
            </div>
            {svc.unit && <div className="text-xs text-muted-foreground">{svc.unit}</div>}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gold">หมวดบริการ</div>
            <div className="mt-1 text-xl font-black text-primary">{cat?.label}</div>
            <div className="text-xs text-muted-foreground">{cat?.sub}</div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gold">พื้นที่บริการ</div>
            <div className="mt-1 text-xl font-black text-primary">ทั่ว{config.nameTh}</div>
            <div className="text-xs text-muted-foreground">On-site ถึงโรงแรม คอนโด วิลล่า มารีน่า</div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-primary">รายละเอียดบริการ</h2>
          <ul className="mt-4 grid gap-3 md:grid-cols-2">
            <li className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> ดำเนินการโดยทนายความผู้ทำคำรับรอง / นักแปลขึ้นทะเบียน</li>
            <li className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> รองรับ English / 中文 / Русский / Deutsch / Français</li>
            <li className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> นัด on-site ถึงที่พักภายใน{config.nameTh}</li>
            <li className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> ออกใบเสร็จและใบกำกับภาษีได้</li>
          </ul>
        </section>

        {segments.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary">กลุ่มลูกค้าที่ใช้บริการนี้บ่อย</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {segments.map((seg) => (
                <Link key={seg.slug} to="/for/$segment" params={{ segment: seg.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                  <div className="text-2xl">{seg.emoji}</div>
                  <h3 className="mt-2 text-sm font-bold text-primary group-hover:text-gold">{seg.titleTh}</h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{seg.shortTh}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {areas.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary">พื้นที่ใน{config.nameTh}ที่ให้บริการ</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {areas.map((a) => (
                <Link key={a.slug} to="/areas/$area" params={{ area: a.slug }} className="rounded-md border border-border bg-secondary/60 px-3 py-1.5 text-xs font-medium hover:border-gold hover:text-gold">{a.nameTh} ({a.nameEn})</Link>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary">บริการอื่นที่เกี่ยวข้อง</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/services/$service" params={{ service: r.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                  <h3 className="text-sm font-bold text-primary group-hover:text-gold">{r.title}</h3>
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูบริการ <ChevronRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <FaqSourceNote topic={sourceTopicForCategory(svc.category)} locale="th" />
      </div>
    </ProvincePageShell>
  );
}